import { SendHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type MessageRole = 'assistant' | 'user';

interface Message {
  id: string;
  role: MessageRole;
  text: string;
}

interface DeepDiveOption {
  option_id: string;
  label: string;
}

interface DeepDiveConfig {
  question: string;
  options: DeepDiveOption[];
}

interface InitialResponse {
  type: 'initial';
  theme: string;
  ack: string;
  answer: string;
  deep_dive: DeepDiveConfig | null;
}

interface PersonalizedResponse {
  type: 'personalized';
  bridge: string;
  reply: string;
  used_story: boolean;
  story_id: string | null;
}

interface EscalationResponse {
  type: 'escalation';
  message: string;
}

interface ErrorResponse {
  error: string;
  message?: string;
}

type ChatResponse = InitialResponse | PersonalizedResponse | EscalationResponse | ErrorResponse;

// Backend (Cloud Run) URL. Hard-coded for the production build so we don't
// have to wire up environment variables in Cloudflare Pages. The env var
// override is kept for previews / staging if needed.
//
// In dev (`npm run dev`), API_BASE stays empty and Vite proxies `/api/*` to
// the local backend on :8000 (see vite.config.ts).
const PROD_API_BASE = 'https://sp-thanks-120787071559.asia-northeast1.run.app';
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (import.meta.env.PROD ? PROD_API_BASE : '');

const SAFETY_NOTE =
  'この会話は選考評価に使用されません。個人が特定される情報の入力は不要です。';

const INITIAL_MESSAGE: Message = {
  id: 'init',
  role: 'assistant',
  text: 'こんにちは。気になることを、そのまま書いてください。言葉がまとまっていなくても大丈夫です。',
};

const THEME_CHIPS = [
  '勤務時間',
  'リモート/出社',
  '評価',
  '研修制度',
  '給与',
  'ワークライフバランス',
  'キャリア',
];

const GENERIC_ERROR_TEXT =
  '通信が混み合っているようです。少し時間をおいて、もう一度ためしていただけますか。';

function uid(role: MessageRole) {
  return `${Date.now()}-${role}-${Math.random().toString(36).slice(2, 7)}`;
}

function isErrorResponse(r: ChatResponse): r is ErrorResponse {
  return (r as ErrorResponse).error !== undefined;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

export default function Conversation() {
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [deepDive, setDeepDive] = useState<DeepDiveConfig | null>(null);
  const [showFreeText, setShowFreeText] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [otherOption, setOtherOption] = useState<DeepDiveOption | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Bootstrap a session on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await postJson<{ session_id: string }>('/api/session', {});
        if (!cancelled) setSessionId(data.session_id);
      } catch {
        // Session will be lazy-created on first /api/chat request server-side.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const messageArea = messagesRef.current;
    if (!messageArea) return;
    messageArea.scrollTo({ top: messageArea.scrollHeight, behavior: 'smooth' });
  }, [messages, isTyping, deepDive]);

  const appendMessage = (role: MessageRole, text: string) => {
    setMessages((prev) => [...prev, { id: uid(role), role, text }]);
  };

  const renderResponse = (data: ChatResponse) => {
    if (isErrorResponse(data)) {
      appendMessage('assistant', data.message ?? GENERIC_ERROR_TEXT);
      return;
    }

    if (data.type === 'escalation') {
      appendMessage('assistant', data.message);
      setDeepDive(null);
      return;
    }

    if (data.type === 'initial') {
      const combined = [data.ack, data.answer].filter(Boolean).join('\n\n');
      if (combined) appendMessage('assistant', combined);
      setDeepDive(data.deep_dive);
      return;
    }

    if (data.type === 'personalized') {
      const combined = [data.bridge, data.reply].filter(Boolean).join('\n\n');
      if (combined) appendMessage('assistant', combined);
      setDeepDive(null);
      return;
    }
  };

  const sendChat = async (text: string, themeId?: string) => {
    if (isTyping) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    appendMessage('user', trimmed);
    setInput('');
    setDeepDive(null);
    setShowFreeText(false);
    setOtherOption(null);
    setFreeText('');
    setIsTyping(true);

    try {
      const data = await postJson<ChatResponse>('/api/chat', {
        session_id: sessionId,
        message: trimmed,
        theme_id: themeId,
      });
      renderResponse(data);
    } catch {
      appendMessage('assistant', GENERIC_ERROR_TEXT);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const sendDeepDive = async (option: DeepDiveOption, extraText = '') => {
    if (isTyping) return;
    const userEcho = extraText ? `${option.label}: ${extraText}` : option.label;
    appendMessage('user', userEcho);
    setDeepDive(null);
    setShowFreeText(false);
    setOtherOption(null);
    setFreeText('');
    setIsTyping(true);

    try {
      const data = await postJson<ChatResponse>('/api/chat/deepdive', {
        session_id: sessionId,
        option_id: option.option_id,
        option_label: option.label,
        free_text: extraText,
      });
      renderResponse(data);
    } catch {
      appendMessage('assistant', GENERIC_ERROR_TEXT);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleDeepDiveClick = (option: DeepDiveOption) => {
    // "その他" options end with `_other` — show a free-text field for them.
    if (option.option_id.endsWith('_other')) {
      setOtherOption(option);
      setShowFreeText(true);
      return;
    }
    sendDeepDive(option);
  };

  const handleFreeTextSend = () => {
    if (!otherOption) return;
    sendDeepDive(otherOption, freeText.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChat(input);
    }
  };

  return (
    <section className="bg-[#F7F4EE] px-5 pt-3 pb-16 sm:px-8 sm:pt-5 sm:pb-24">
      <div className="mx-auto w-full max-w-4xl">
        <div className="overflow-hidden rounded-lg border border-[#E8E1D8] bg-white shadow-[0_18px_46px_rgba(6,1,72,0.08)]">
          <div className="border-b border-[#E8E1D8] bg-white px-4 py-5 sm:px-7 sm:py-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[18px] font-bold leading-[1.35] text-[#060148] sm:text-[20px]">
                  Special Thanks 採用AI相談
                </p>
                <p className="mt-2 max-w-2xl text-[14px] font-medium leading-[1.7] text-[#444444] sm:text-[15px]">
                  生成AIの回答には誤りが含まれる場合があります。正確な情報は採用サイトや面接時にご確認ください。
                </p>
              </div>
              <span className="hidden h-2 w-2 flex-none rounded-full bg-[#E85B63] sm:block" />
            </div>
          </div>

          <div className="flex h-[470px] max-h-[62vh] min-h-[430px] flex-col bg-[#FFFDF9] sm:h-[620px] sm:max-h-[74vh] sm:min-h-[500px]">
            <div ref={messagesRef} className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-8">
              <div className="flex flex-col gap-5">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end pl-6 sm:pl-14' : 'justify-start pr-6 sm:pr-14'}`}
                  >
                    <div
                      className={`max-w-[92%] whitespace-pre-wrap rounded-lg px-4 py-4 text-[16px] font-medium leading-[1.75] sm:max-w-[80%] sm:px-5 sm:text-[17px] ${
                        msg.role === 'user'
                          ? 'bg-[#060148] text-white'
                          : 'border border-[#E8E1D8] bg-[#F3F0EA] text-[#222222]'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}

                {deepDive && !isTyping && (
                  <div className="flex flex-col gap-3 rounded-lg border border-[#E8E1D8] bg-white px-4 py-4 sm:px-5">
                    <p className="text-[15px] font-semibold leading-[1.6] text-[#060148] sm:text-[16px]">
                      {deepDive.question}
                    </p>
                    <div className="flex flex-col gap-2">
                      {deepDive.options.map((opt) => (
                        <button
                          key={opt.option_id}
                          type="button"
                          onClick={() => handleDeepDiveClick(opt)}
                          disabled={isTyping}
                          className="min-h-11 rounded-lg border border-[#E8E1D8] bg-[#F7F4EE] px-4 py-2 text-left text-[15px] font-medium leading-[1.6] text-[#222222] transition-colors duration-200 hover:border-[#E85B63] hover:text-[#060148] disabled:cursor-not-allowed disabled:opacity-50 sm:text-[16px]"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {showFreeText && (
                      <div className="flex flex-col gap-2 pt-1">
                        <textarea
                          value={freeText}
                          onChange={(e) => setFreeText(e.target.value)}
                          placeholder="もう少し具体的に教えてください"
                          rows={2}
                          maxLength={500}
                          className="min-h-[56px] resize-none rounded-lg border border-[#E8E1D8] bg-white px-3 py-2 text-[15px] leading-[1.7] text-[#222222] outline-none placeholder:text-[#888888] focus:border-[#E85B63] sm:text-[16px]"
                        />
                        <button
                          type="button"
                          onClick={handleFreeTextSend}
                          disabled={!freeText.trim() || isTyping}
                          className="self-end rounded-lg bg-[#E85B63] px-5 py-2 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#d94e57] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          送信する
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {isTyping && (
                  <div className="flex justify-start pr-10">
                    <div className="flex rounded-lg border border-[#E8E1D8] bg-[#F3F0EA] px-5 py-5">
                      <span className="mx-1 h-1.5 w-1.5 animate-bounce rounded-full bg-[#060148]/40 [animation-delay:0ms]" />
                      <span className="mx-1 h-1.5 w-1.5 animate-bounce rounded-full bg-[#060148]/40 [animation-delay:150ms]" />
                      <span className="mx-1 h-1.5 w-1.5 animate-bounce rounded-full bg-[#060148]/40 [animation-delay:300ms]" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-[#E8E1D8] bg-white px-4 py-3 sm:px-7">
              <div className="flex gap-2 overflow-x-auto pb-1 sm:gap-3" aria-label="相談したいテーマ">
                {THEME_CHIPS.map((theme) => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => sendChat(theme, theme)}
                    disabled={isTyping}
                    className="min-h-11 flex-none rounded-lg border border-[#E8E1D8] bg-white px-4 py-2 text-left text-[16px] font-medium leading-[1.6] text-[#222222] transition-colors duration-200 hover:border-[#E85B63] hover:text-[#060148] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {theme}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-[#E8E1D8] bg-white p-3 sm:p-4">
              <div className="flex items-end gap-3">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="気になることを書いてください"
                  rows={2}
                  maxLength={500}
                  className="min-h-[56px] flex-1 resize-none bg-transparent text-[16px] font-medium leading-[1.75] text-[#222222] outline-none placeholder:text-[#555555] sm:text-[17px]"
                />
                <button
                  type="button"
                  onClick={() => sendChat(input)}
                  disabled={!input.trim() || isTyping}
                  aria-label="送信"
                  className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-[#E85B63] text-white transition-colors duration-200 hover:bg-[#d94e57] disabled:cursor-not-allowed disabled:opacity-50 sm:h-[52px] sm:w-[52px]"
                >
                  <SendHorizontal size={20} strokeWidth={2.2} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="mx-auto mt-5 max-w-3xl text-center text-[16px] font-semibold leading-[1.75] text-[#E85B63]">
          {SAFETY_NOTE}
        </p>
      </div>
    </section>
  );
}
